import groupBy from 'lodash/groupBy';
import React, { ReactElement, useEffect } from 'react';
import { twMerge } from 'tailwind-merge';

import { Telemetry } from '@videoblocks/kafka-rest-client';
import { Tab } from '@videoblocks/storywind';

import { JustifiedGallery } from '../../../app/Layouts/JustifiedGallery/JustifiedGallery';
import {
  JUSTIFIED_GALLERY_DEFAULT_HEIGHTS,
  VERTICAL_JUSTIFIED_GALLERY_HEIGHTS,
} from '../../../app/Layouts/JustifiedGallery/constants';
import MissingAudioStockItemCard from '../../../app/Search/components/card/MissingAudioStockItemCard';
import MissingImageStockItemCard from '../../../app/Search/components/card/MissingImageStockItemCard';
import MissingVideoStockItemCard from '../../../app/Search/components/card/MissingVideoStockItemCard';
import { stockItemCardForContentClass } from '../../../app/Search/components/card/StockItemCard';
import { ContentClass } from '../../../app/Shared/enums';
import { areAllItemsVertical } from '../../../common/stockItems/utils';
import { StockItemContext } from '../../../common/types/StockItemTypes';
import { AUDIO } from '../../../common/utils';
import CheckboxWrapper from '../../MemberBin/CheckboxWrapper';
import ReorderingWrapper from '../../MemberBin/ReorderingWrapper';
import { FolderItemWithUIState, RemovedStockItem } from '../types';
import NothingHero from './NothingHero';

const contentClassToTabTitle: Record<ContentClass, string> = {
  [ContentClass.Video]: 'Video',
  [ContentClass.Audio]: 'Audio',
  [ContentClass.Image]: 'Images',
};

const contentClassToSearchPath: Record<ContentClass, string> = {
  [ContentClass.Video]: '/all-video/search',
  [ContentClass.Audio]: '/audio/search',
  [ContentClass.Image]: '/images/search',
};

export interface FolderContentTabsProps {
  /**
   * If true, we show tabs with no content. A call to action prompts you to add items to the folder.
   */
  allowEmptyTabs: boolean;
  /**
   * If true, render checkboxes for selecting cards.
   */
  allowSelections: boolean;
  /**
   * If true, show the download button on cards.
   */
  allowDownloads: boolean;
  folderItems: FolderItemWithUIState[];
  similarItems?: FolderItemWithUIState[];
  inReorderingMode?: boolean;
  onReorderDrop?: (
    draggedContentId: number,
    destinationContentId: number,
    shouldInsertBefore: boolean
  ) => void;
  removedStockItems: RemovedStockItem[];
  /**
   * For informational purposes only. This is an uncontrolled component.
   * @param contentClass
   */
  onTabChange?: (contentClass: ContentClass) => void;
}

/**
 * Tab group that renders the contents of a folder.
 * @param allowEmptyTabs
 * @param allowSelections
 * @param allowDownloads
 * @param folderItems
 * @param similarItems
 * @param inReorderingMode
 * @param onReorderDrop
 * @param onTabChange
 * @param removedStockItems
 * @constructor
 */
export default function FolderContentTabs({
  allowEmptyTabs,
  allowSelections,
  allowDownloads,
  folderItems,
  similarItems = [],
  inReorderingMode = false,
  onReorderDrop = () => null,
  onTabChange = () => null,
  removedStockItems,
}: FolderContentTabsProps): ReactElement {
  const removedStockItemsByContentClass: Record<
    ContentClass,
    FolderItemWithUIState[]
  > = {
    [ContentClass.Video]: [],
    [ContentClass.Audio]: [],
    [ContentClass.Image]: [],
    ...groupBy(removedStockItems, 'contentClass'),
  };

  const itemsByContentClass: Record<ContentClass, FolderItemWithUIState[]> = {
    [ContentClass.Video]: [],
    [ContentClass.Audio]: [],
    [ContentClass.Image]: [],
    ...groupBy(folderItems, 'stockItem.contentClass'),
  };

  const hasContent = (contentClass: ContentClass): boolean =>
    itemsByContentClass[contentClass].length > 0;
  const shouldShowTab = (contentClass: ContentClass): boolean =>
    allowEmptyTabs || hasContent(contentClass);

  const tabContentClasses: ContentClass[] = [
    ContentClass.Video,
    ContentClass.Audio,
    ContentClass.Image,
  ].filter(shouldShowTab);

  // Find the first tab with any stock items in it.
  // Default to zero if no such index was found.
  const defaultTabIndex: number = tabContentClasses
    .map((contentClass, index) => ({
      eligible: hasContent(contentClass),
      index,
    }))
    .filter(({ eligible }) => eligible)
    .map(({ index }) => index)
    // Add 0 to the end of the array so we default to zero if there are otherwise no eligible tabs.
    .concat(0)
    // Snag the first element of the array.
    .shift();

  useEffect(() => {
    onTabChange(tabContentClasses[defaultTabIndex]);
  }, [defaultTabIndex]);

  const renderStockItemCardInner = (
    stockItemInfo,
    shouldShowAddToFolder = false,
    usesLargeVerticalLayout: boolean
  ) => {
    const StockItemCard = stockItemCardForContentClass(
      stockItemInfo.stockItem.contentClass
    );

    const relatedContentAddToFolderTelemetry = () => {
      if (shouldShowAddToFolder) {
        return Telemetry.increment('favorite.clicked.related-content');
      }
      return undefined;
    };

    return (
      <StockItemCard
        key={`stock-item-card-${stockItemInfo.stockItem.id}`}
        usesLargeVerticalLayout={usesLargeVerticalLayout}
        stockItem={stockItemInfo.stockItem}
        shouldStockItemHaveNewFlag={false}
        page={stockItemInfo.page}
        context={StockItemContext.FOLDER}
        isCollapsedStockItemLoadMoreCard={false}
        draggableCard={inReorderingMode}
        hideOnThreeColumnLayout={false}
        shouldShowDownloadButton={allowDownloads}
        shouldShowAddToFavorites={shouldShowAddToFolder}
        downloadButtonTelemetryAction={() =>
          Telemetry.increment('favorite.download')
        }
        stockItemArtists={stockItemInfo.stockItemArtists}
        style={{}}
        classNames={['focus-within:z-10']}
        imgClassNames={[]}
        containerResponsiveClassNames={['']}
        stockItemFormats={stockItemInfo.stockItemFormats}
        makerButtonTelemetryAction={() =>
          Telemetry.increment('favorite.edit-with-maker')
        }
        favoriteButtonTelemetryAction={() =>
          relatedContentAddToFolderTelemetry()
        }
        videoStyles={{ width: '100%', height: '100%' }}
      />
    );
  };

  const renderStockItemCardWithWrappers = (
    stockItemInfo,
    usesLargeVerticalLayout: boolean
  ) => {
    const stockItemCard = renderStockItemCardInner(
      stockItemInfo,
      false,
      usesLargeVerticalLayout
    );

    if (inReorderingMode) {
      return (
        <ReorderingWrapper
          key={`reorder-wrapper-${stockItemInfo.stockItem.id}`}
          stockItemId={stockItemInfo.stockItem.contentId}
          dropOntoHandler={onReorderDrop}
          useVerticalDrag={stockItemInfo.stockItem.contentClass === AUDIO}
        >
          {stockItemCard}
        </ReorderingWrapper>
      );
    }

    if (allowSelections) {
      return (
        <CheckboxWrapper
          key={`checkbox-wrapper-${stockItemInfo.stockItem.id}`}
          checked={stockItemInfo.checked}
          stockItem={stockItemInfo.stockItem}
        >
          {stockItemCard}
        </CheckboxWrapper>
      );
    }
    return stockItemCard;
  };

  const renderFolderContents = (contentClass: ContentClass) => {
    const items = itemsByContentClass[contentClass];
    const removedItems = removedStockItemsByContentClass[contentClass];

    if (items.length + removedItems.length === 0) {
      return null;
    }

    const isAudio: boolean = contentClass === ContentClass.Audio;

    const missingStockItemCards = removedItems.map((_, index) => {
      if (contentClass === ContentClass.Image) {
        return <MissingImageStockItemCard key={index} />;
      }

      if (contentClass === ContentClass.Audio) {
        return <MissingAudioStockItemCard key={index} />;
      }

      if (contentClass === ContentClass.Video) {
        return <MissingVideoStockItemCard key={index} />;
      }
    });

    const shouldUseJustifiedGallery = contentClass === ContentClass.Video;

    const renderWithJustifiedGallery = (items: FolderItemWithUIState[]) => {
      const allItemsAreVertical = areAllItemsVertical(items);
      const responsiveHeights = allItemsAreVertical
        ? VERTICAL_JUSTIFIED_GALLERY_HEIGHTS
        : JUSTIFIED_GALLERY_DEFAULT_HEIGHTS;

      return (
        <div
          id="bin-contents-container"
          className="stock-item-group-wrapper card-design content-info mt-12"
        >
          <JustifiedGallery
            items={items.map((item) => ({
              id: item.stockItem.id,
              aspectRatio: item.stockItem.aspectRatio,
            }))}
            responsiveHeights={responsiveHeights}
          >
            {({ index }) => {
              const item = items[index];
              return renderStockItemCardWithWrappers(item, allItemsAreVertical);
            }}
          </JustifiedGallery>
        </div>
      );
    };

    // renders grid for non-video content
    const renderWithNormalGrid = (items: FolderItemWithUIState[]) => {
      return (
        <div
          id="bin-contents-container"
          className={twMerge(
            !isAudio && 'mb-12 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4',
            'stock-item-group-wrapper card-design content-info grid mt-14 mb-8 grid-cols-1 gap-2'
          )}
        >
          {items.map((stockItemInfo) =>
            renderStockItemCardWithWrappers(stockItemInfo, false)
          )}
          {missingStockItemCards}
        </div>
      );
    };

    return shouldUseJustifiedGallery
      ? renderWithJustifiedGallery(items)
      : renderWithNormalGrid(items);
  };

  return (
    <Tab.Group
      defaultIndex={defaultTabIndex}
      onChange={(index) => onTabChange(tabContentClasses[index])}
    >
      <Tab.List>
        {tabContentClasses.map((contentClass) => (
          <Tab
            key={contentClass}
            label={contentClassToTabTitle[contentClass]}
          />
        ))}
      </Tab.List>
      <Tab.Panels>
        {tabContentClasses.map((contentClass) => {
          const allItemsAreVertical = areAllItemsVertical(similarItems);
          const responsiveHeights = allItemsAreVertical
            ? VERTICAL_JUSTIFIED_GALLERY_HEIGHTS
            : JUSTIFIED_GALLERY_DEFAULT_HEIGHTS;
          return (
            <Tab.Panel key={contentClass}>
              {renderFolderContents(contentClass) || (
                <NothingHero
                  contentType={contentClass}
                  route={contentClassToSearchPath[contentClass]}
                />
              )}

              {contentClass === ContentClass.Video &&
                similarItems.length > 0 && (
                  <div id="related-content-container">
                    <h3>Related Content</h3>
                    <div className="mb-12 stock-item-group-wrapper card-design content-info mt-14 mb-8">
                      <JustifiedGallery
                        items={similarItems.map((similarItem) => ({
                          id: similarItem.stockItem.id,
                          aspectRatio: similarItem.stockItem.aspectRatio,
                        }))}
                        responsiveHeights={responsiveHeights}
                      >
                        {({ index }) => {
                          const similarItem = similarItems[index];
                          return renderStockItemCardInner(
                            similarItem,
                            true,
                            allItemsAreVertical
                          );
                        }}
                      </JustifiedGallery>
                    </div>
                  </div>
                )}
            </Tab.Panel>
          );
        })}
      </Tab.Panels>
    </Tab.Group>
  );
}
